import { parsePeriodId } from '@artem-cbam-declarants/shared'
import { isAfter, isBefore, differenceInDays, format } from 'date-fns'

/** Calendar quarter label (CBAM goods, liability batch, EUA tooling). */
export type Quarter = 'Q1' | 'Q2' | 'Q3' | 'Q4'

export const QUARTERS: Quarter[] = ['Q1', 'Q2', 'Q3', 'Q4']

/**
 * Quarters for which official 2026 EUA reference prices are published by the EC.
 * Expand this list when additional quarters become available.
 */
export const OFFICIAL_EUA_2026_QUARTERS_WITH_PUBLISHED_PRICE: readonly Quarter[] = [
  'Q1',
  'Q2',
  'Q3',
]

export function getCurrentCalendarQuarter(date = new Date()): Quarter {
  const month = date.getUTCMonth()
  if (month < 3) {
    return 'Q1'
  }
  if (month < 6) {
    return 'Q2'
  }
  if (month < 9) {
    return 'Q3'
  }
  return 'Q4'
}

export function getCurrentEuaPriceYear(date = new Date()): 2026 | 2027 | null {
  const year = date.getUTCFullYear()
  if (year === 2026 || year === 2027) {
    return year
  }
  return null
}

export type CbamReportingDataExportFormat = 'csv' | 'xlsx'

/** Client download filename for reporting data export, e.g. `CBAM-reporting-data_2026_Q1_Q2.xlsx`. */
export function buildCbamReportingDataExportFileName(
  year: number,
  quarters: Quarter[],
  format: CbamReportingDataExportFormat
): string {
  const quartersPart = quarters.join('_')
  return `CBAM-reporting-data_${year}_${quartersPart}.${format}`
}

type CbamReportingPeriod = {
  periodId: string
  label: string
  composeFileName: (version: number) => string
  reportingEndDate: string
  quarter?: number
  year: number
}

type CbamReportStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'PAST_DEADLINE'

const CBAM_REPORTING_PERIODS: CbamReportingPeriod[] = [
  {
    periodId: '2025-07_2025-09',
    label: 'Q3 2025',
    composeFileName: (version: number) => `CBAM_Report_Q3_2025_V${version}.zip`,
    quarter: 3,
    year: 2025,
    reportingEndDate: '2025-10-31'
  },
  {
    periodId: '2025-10_2025-12',
    label: 'Q4 2025',
    composeFileName: (version: number) => `CBAM_Report_Q4_2025_V${version}.zip`,
    quarter: 4,
    year: 2025,
    reportingEndDate: '2026-01-31'
  },
  {
    periodId: '2026-01_2026-12',
    label: '2026',
    composeFileName: (version: number) => `CBAM_Report_2026_V${version}.zip`,
    year: 2026,
    reportingEndDate: '2027-09-30'
  },
  {
    periodId: '2027-01_2027-12',
    label: '2027',
    composeFileName: (version: number) => `CBAM_Report_2027_V${version}.zip`,
    year: 2027,
    reportingEndDate: '2028-09-30'
  }
]

// Convert array to lookup object for backward compatibility
export const cbamReportingPeriodConfig = CBAM_REPORTING_PERIODS.reduce(
  (config, period) => {
    config[period.periodId] = {
      label: period.label,
      value: period.periodId,
      reportingEndDate: period.reportingEndDate,
      year: period.year,
      composeFileName: period.composeFileName
    }
    return config
  },
  {} as Record<
    string,
    {
      label: string
      value: string
      reportingEndDate: string
      year: number
      composeFileName: (version: number) => string
    }
  >
)

export const getCbamReportStatus = (
  periodId: string,
  isCompleted: boolean,
  reportingEndDate?: string
): CbamReportStatus => {
  // If completed, always return COMPLETED regardless of dates
  if (isCompleted) {
    return 'COMPLETED'
  }

  // Get period configuration
  const period = getCbamReportingPeriod(periodId)
  if (!period) {
    console.warn(`Unknown CBAM reporting period: ${periodId}`)
    return 'NOT_STARTED'
  }

  // Use provided reportingEndDate or fall back to config
  const endDate = reportingEndDate || period.reportingEndDate
  const { startDate } = parsePeriodId(periodId)
  const now = new Date()

  // Check if we're past the reporting deadline
  if (isAfter(now, new Date(endDate))) {
    return 'PAST_DEADLINE' // Past deadline, should be completed but isn't
  }

  // Check if the reporting period has started
  if (isBefore(now, startDate)) {
    return 'NOT_STARTED'
  }

  // We're within the reporting period
  return 'IN_PROGRESS'
}

// Enhanced version that takes a CBAM report object directly
export const getCbamReportStatusFromReport = (cbamReport: {
  periodId: string
  isCompleted: boolean
}): CbamReportStatus => {
  const period = getCbamReportingPeriod(cbamReport.periodId)
  if (!period) {
    console.warn(`Unknown CBAM reporting period: ${cbamReport.periodId}`)
    return 'NOT_STARTED'
  }

  return getCbamReportStatus(cbamReport.periodId, cbamReport.isCompleted, period.reportingEndDate)
}

export const getCbamReportingPeriod = (periodId: string): CbamReportingPeriod | undefined => {
  return CBAM_REPORTING_PERIODS.find(period => period.periodId === periodId)
}

// Enhanced period state calculation with better logic
export const getCbamPeriodInfo = (
  periodId: string,
  isCompleted: boolean
): {
  isOngoing: boolean
  isCompleted: boolean
  deadlinePassed: boolean
  daysLeft: number
  totalDays: number
  formattedEndDate: string
  formattedPeriodEndDate: string
  status: CbamReportStatus
} => {
  const period = getCbamReportingPeriod(periodId)
  if (!period) {
    console.warn(`Unknown CBAM reporting period: ${periodId}`)
    return {
      isOngoing: false,
      isCompleted: false,
      deadlinePassed: false,
      daysLeft: 0,
      totalDays: 0,
      formattedEndDate: '',
      formattedPeriodEndDate: '',
      status: 'NOT_STARTED'
    }
  }

  const { startDate, endDate } = parsePeriodId(periodId)
  const now = new Date()
  const submissionDeadline = new Date(period.reportingEndDate)
  const totalDays = differenceInDays(endDate, startDate)

  // Format dates consistently
  const formatDate = (date: Date) => format(date, 'dd/MM/yyyy')
  const formattedSubmissionDeadline = formatDate(submissionDeadline)
  const formattedPeriodEndDate = formatDate(endDate)

  // Determine status using our improved logic
  const status = getCbamReportStatus(periodId, isCompleted, period.reportingEndDate)

  // Calculate state based on status
  switch (status) {
    case 'COMPLETED':
      return {
        isOngoing: false,
        isCompleted: true,
        deadlinePassed: isAfter(now, submissionDeadline),
        daysLeft: 0,
        totalDays,
        formattedEndDate: formattedSubmissionDeadline,
        formattedPeriodEndDate,
        status
      }

    case 'IN_PROGRESS':
      const daysLeft = differenceInDays(endDate, now)
      return {
        isOngoing: true,
        isCompleted: false,
        deadlinePassed: false,
        daysLeft: Math.max(0, daysLeft),
        totalDays,
        formattedEndDate: formattedSubmissionDeadline,
        formattedPeriodEndDate,
        status
      }

    case 'NOT_STARTED':
    default:
      return {
        isOngoing: false,
        isCompleted: false,
        deadlinePassed: isAfter(now, submissionDeadline),
        daysLeft: 0,
        totalDays,
        formattedEndDate: formattedSubmissionDeadline,
        formattedPeriodEndDate,
        status
      }
  }
}
